import React, { PropTypes, PureComponent } from 'react';

import { googlePlaceShape } from '../../proptypes';

import { withAsyncScript } from '../AsyncScriptWrapper/AsyncScriptWrapper.react';

import { text } from '../../base-styles/forms.module.css';
import { getCountryViewport } from '../../utils/geoLocation';
import getUKViewport, { ukPlaceId } from './getUKViewport';


/**
 * Place picker component
 * Provides an autocomplete input field for Google Places API
 * Calls method on change consistent with other form inputs
 * Read more: https://developers.google.com/maps/documentation/javascript/places-autocomplete
 */
class Placepicker extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      // Value is a full Place object, just show the name if it's set
      text: props.value ? (props.value.address_formatted || props.value.name) : '',
    };
  }

  componentDidMount() {
    this.init();
  }

  componentDidUpdate(prevProps) {
    // Lazily compare whether types have updated
    if (this.props.types.join('') !== prevProps.types.join('')) {
      if (this.autocomplete) {
        this.input.value = '';
        this.autocomplete.setOptions({
          ...this.options,
          types: this.props.types,
        });
      }
    }
  }

  init = () => {
    const {
      googleAPI,
      types,
      countryCode,
    } = this.props;

    let countryViewport = getCountryViewport(countryCode);
    if (!countryViewport) {
      countryViewport = {
        viewportNorthEastLat: 49.3836,
        viewportNorthEastLong: -8.593005,
        viewportSouthWestLat: 60.889515,
        viewportSouthWestLong: 1.642589,
      };
    }

    if (googleAPI) {
      // Will set the preferred area within which to return Place results.
      // Results are biased towards, but not restricted to, the UK.
      this.options = {
        types,
        bounds: new googleAPI.maps.LatLngBounds(
          new googleAPI.maps.LatLng(
            countryViewport.viewportNorthEastLat, countryViewport.viewportNorthEastLong,
          ),
          new googleAPI.maps.LatLng(
            countryViewport.viewportSouthWestLat, countryViewport.viewportSouthWestLong,
          ),
       ),
      };
      this.autocomplete = new googleAPI.maps.places.Autocomplete(this.input, this.options);
      // Trigger when place is selected from options, input is submitted
      this.autocomplete.addListener('place_changed', this.handleSelect);
      googleAPI.maps.event.addDomListener(this.input, 'keydown', (event) => {
        if (event.keyCode === 13) {
          event.preventDefault();
        }
      });
    }
  }

  handleChange = (event) => {
    const { onChange } = this.props;
    this.setState({ text: event.target.value });
    onChange({
      target: { name: this.input.name, value: null },
      type: 'change',
    });
  }

  handleSelect = () => {
    const { onChange } = this.props;
    const { name, value } = this.input;
    const place = this.autocomplete.getPlace();
    // Only handle valid place objects
    if (place.place_id) {
      if (place.place_id === ukPlaceId) {
        place.geometry.viewport = getUKViewport();
      }
      // Google API will populate the input for us, capture this in state for controlled input
      this.setState({ text: value });
      onChange({
        target: { name, value: place },
        type: 'change',
      });
    }
  }

  render() {
    const {
      className,
      googleAPI,
      placeholder,
      types,
      value,
      countryCode,
      ...props
    } = this.props;
    if (!googleAPI) {
      return <p>Loading</p>;
    }

    return (
      <input
        {...props}
        className={[text, className].join(' ')}
        onChange={this.handleChange}
        placeholder={placeholder}
        value={value ? (value.address_formatted || value.name) : this.state.text}
        ref={(node) => { this.input = node; }}
      />
    );
  }
}

Placepicker.propTypes = {
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Google Maps API object, supplied by async script wrapper component (for client only)
   */
  googleAPI: PropTypes.shape(),
  /**
   * Function called on place change or select
   */
  onChange: PropTypes.func,
  /**
   * String for text input placeholder.
   */
  placeholder: PropTypes.string,
  /**
   * Array of prediction types to be returned
   * see https://developers.google.com/maps/documentation/javascript/reference#AutocompletionRequest
   */
  types: PropTypes.arrayOf(PropTypes.oneOf([
    'establishment',
    'geocode',
    '(regions)',
    '(cities)',
  ]).isRequired),
  /**
   * Google Place object, from which formatted address is shown
   */
  value: googlePlaceShape,
  countryCode: PropTypes.string,
};

Placepicker.defaultProps = {
  className: '',
  googleAPI: undefined,
  onChange: () => {},
  placeholder: 'Search Location',
  types: ['(regions)'],
  value: undefined,
  countryCode:'',
};

// Configuration of async script requirement
const asyncScriptProps = {
  // ID given to script tag in index
  id: 'gmaps',
  // Global variable name when ready
  global: 'google',
  // Name to deliver global as prop (ensure loaded scope)
  propName: 'googleAPI',
};

// Wrap with async script loader, which adds global object as prop when ready
const PlacepickerWithAsync = withAsyncScript(Placepicker, asyncScriptProps);

export default PlacepickerWithAsync;

// Export unwrapped class for tests
export const PlacepickerClass = Placepicker;



// WEBPACK FOOTER //
// ./app/src/components/Placepicker/Placepicker.react.js